import Controller, { inject as controller } from '@ember/controller';
import { action, set } from '@ember/object';
import { trackedObject } from '@ember/reactive/collections';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { PLATFORMS } from 'frontend-utils/utils/constants/constant-values';
import {
  ISSUE_TYPE_TRANSLATIONS,
  ISSUE_TYPES,
} from 'lumen/models/parity-manager/issue';
import { PLATFORM_TRANSLATION_KEYS } from 'lumen/utils/constants/general-constants';
import { PLATFORM_PRETTY_NAMES } from 'lumen/utils/constants/pi-constant-values';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';

const MANUAL_ISSUE_STEPS = {
  MAJOR_OTA: {
    HOTEL_INFO: 1,
    CHANNEL_INFO: 2,
    OTHER_INFO: 3,
    ATTACHMENTS: 4,
  },
  TEST_BOOKING: {
    HOTEL_INFO: 1,
    CHANNEL_INFO: 2,
    BOOKING_DETAILS: 3,
    OTHER_INFO: 4,
    ATTACHMENTS: 5,
  },
  BAIT_AND_SWITCH: {
    HOTEL_INFO: 1,
    META_INFO: 2,
    CHANNEL_INFO: 3,
    OTHER_INFO: 4,
    ATTACHMENTS: 5,
  },
};

export default class MarketParityParityManagerAddissueController extends Controller {
  @service ajax;
  @service alerting;
  @service errorTracking;
  @service('hotel-and-market') hotelAndMarketService;
  @service intl;
  @service router;
  @service toast;
  @service usageAnalytics;

  @controller('market.parity.parity-manager') parentController;

  @tracked newIssue = this._createDefaultIssue();
  @tracked step = this.manualIssueSteps.HOTEL_INFO;
  @tracked uploadingIssue = false;

  get isBaitAndSwitchView() {
    return this.parentController?.isBaitAndSwitchView;
  }

  get isMajorOtaView() {
    return this.parentController?.isMajorOtaView;
  }

  get issueFormTitle() {
    return this.intl.t(
      ISSUE_TYPE_TRANSLATIONS[this.parentController.selectedMetric],
    );
  }

  get isTestBookingView() {
    return this.parentController?.isTestBookingView;
  }

  get manualIssueSteps() {
    const key = Object.keys(ISSUE_TYPES).find(
      (k) => ISSUE_TYPES[k] === this.parentController.selectedMetric,
    );

    return MANUAL_ISSUE_STEPS[key] ?? null;
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get platformOptions() {
    return [
      PLATFORMS.PLATFORM_DESKTOP,
      PLATFORMS.PLATFORM_MOBILE_WEBSITE,
      PLATFORMS.PLATFORM_MOBILE_APP,
    ].map((platform) => ({
      id: platform,
      label: this.intl.t(PLATFORM_TRANSLATION_KEYS[platform]),
    }));
  }

  get subscriptionCurrency() {
    const subscriptionCurrency =
      this.newIssue?.subscription?.subscription_currency;
    const marketCurrency = this.hotelAndMarketService.market?.currencyCode;
    // Until the currency code is fetched from the selected subscription, display the market currency
    return subscriptionCurrency || marketCurrency;
  }

  _createDefaultIssue() {
    const arrivalDate = this.hotelAndMarketService.getLocalTime();

    return trackedObject({
      arrival_date: arrivalDate,
      base_cancellation: false,
      base_platform: PLATFORMS.PLATFORM_DESKTOP,
      channel_cancellation: false,
      channel_platform: PLATFORMS.PLATFORM_DESKTOP,
      los: 1,
      pos: {
        id: 'US',
        name: 'United States',
      },
    });
  }

  loadAdditionalInfoFormData(formData, newIssue) {
    const statusId = newIssue.status?.id;
    const rootCauseId = newIssue.root_cause?.id;
    const assigneeId = newIssue.assignee?.id;

    if (assigneeId) {
      formData.append('assignee_id', assigneeId);
    }

    if (statusId) {
      formData.append('status', statusId);
    }

    if (rootCauseId) {
      formData.append('root_cause_id', rootCauseId);
    }

    formData.append('comment', newIssue.comment ?? '');

    if (!this.parentController.isBaitAndSwitchView) {
      formData.append('rate_plan', newIssue.rate_code ?? '');
    }
  }

  loadAttachmentFormData(formData, newIssue) {
    // set to true in case the attachments are optional
    newIssue.areAllSelectedFilesWithinMaxFileSize = true;

    if (this.isMajorOtaView) {
      formData.append('base_screenshot', newIssue.base_screenshot);
      formData.append('channel_screenshot', newIssue.channel_screenshot);
      formData.append(
        'extranet_screenshot',
        newIssue.extranet_screenshot || null,
      ); // with null meaning it's optional
      formData.append(
        'additional_screenshot',
        newIssue.additional_screenshot || null,
      );
    } else if (this.isTestBookingView) {
      formData.append('brand_screenshot', newIssue.brand_screenshot);
      formData.append(
        'brand_final_checkout_page',
        newIssue.brand_final_checkout_page || null,
      );
      formData.append(
        'channel_final_checkout_page',
        newIssue.channel_final_checkout_page,
      );
      formData.append(
        'metasearch_screenshot',
        newIssue.metasearch_screenshot || null,
      );
      formData.append(
        'booking_reservation_file',
        newIssue.booking_reservation_file,
      );
      formData.append(
        'cancellation_proof',
        newIssue.cancellation_proof || null,
      );
      formData.append(
        'cancellation_policy',
        newIssue.cancellation_policy || null,
      );
      formData.append(
        'booking_reservation_file',
        newIssue.booking_reservation_file || null,
      );
    } else if (this.isBaitAndSwitchView) {
      formData.append(
        'meta_rate_screenshot',
        newIssue.meta_rate_screenshot || null,
      );
      formData.append('brand_rate_screenshot', newIssue.brand_rate_screenshot);
      formData.append(
        'displayed_channel_rate_screenshot',
        newIssue.displayed_channel_rate_screenshot,
      );
      formData.append(
        'actual_channel_rate_screenshot',
        newIssue.actual_channel_rate_screenshot,
      );
    }
  }

  loadBookingDetailsFormData(formData, newIssue) {
    formData.append('guest_first_name', newIssue.guest_first_name);
    formData.append('guest_last_name', newIssue.guest_last_name);
    formData.append('booking_id', newIssue.booking_id);
    formData.append(
      'cancellation_policy',
      newIssue.cancellation_policy || null,
    );
    formData.append('cancel_before', newIssue.cancel_before || '');
    // formData.append('payment_type', newIssue.payment_type?.id || '');
    // formData.append('credit_card', newIssue.credit_card_number || null);
  }

  loadChannelRateFormData(formData, newIssue) {
    formData.append('channel_id', newIssue.channel.id);
    formData.append('channel_rate', newIssue.channel_rate);
    formData.append('channel_room_name', newIssue.channel_room_name ?? '');
    formData.append('channel_cancellation', newIssue.channel_cancellation);

    if (this.isMajorOtaView) {
      formData.append('channel_platform', newIssue.channel_platform);
    }
  }

  loadHotelRateFormData(formData, newIssue) {
    const arrivalDate = this.hotelAndMarketService.toLocalTimeWithFallback(
      newIssue.arrival_date,
    );

    // issue_type is a required param to be able to add an issue; it's included in the hotel rate because all current metrics contain this form.
    formData.append('issue_type', this.parentController.selectedMetric);
    formData.append('subscription_id', newIssue.subscription.id);
    formData.append('rate_type', newIssue.rate.id);
    formData.append('base_rate', newIssue.base_rate);
    formData.append('base_cancellation', newIssue.base_cancellation);
    formData.append('base_room_name', newIssue.base_room_name ?? '');
    formData.append('arrival_date', arrivalDate.format('YYYY-MM-DD')); // Send the date in the YYYY-MM-DD format
    formData.append('los', newIssue.los);

    if (this.isMajorOtaView) {
      formData.append('base_platform', newIssue.base_platform);
      formData.append('pos', newIssue.pos.id);
    }
  }

  loadMetaRateFormData(formData, newIssue) {
    formData.append('metasearch_channel', newIssue.metasearch_channel.id);
    formData.append('meta_rate', newIssue.meta_rate);
    formData.append('meta_room_name', newIssue.meta_room_name ?? '');
  }

  @action
  back() {
    this.step -= 1;
  }

  @action
  exitForm() {
    this.step = this.manualIssueSteps.HOTEL_INFO;
    this.newIssue = this._createDefaultIssue();
    this.router.legacyTransitionTo('market.parity.parity-manager');
  }

  @action
  next() {
    this.step += 1;
  }

  @action
  redirectOnClose() {
    this.exitForm();
  }

  @action
  saveIssue() {
    this.uploadingIssue = true;
    const formData = new FormData();
    const { newIssue } = this;

    // Hotel rate form
    this.loadHotelRateFormData(formData, newIssue);

    // Channel rate form
    this.loadChannelRateFormData(formData, newIssue);

    // Meta rate form
    if (this.isBaitAndSwitchView) {
      this.loadMetaRateFormData(formData, newIssue);
    }

    // Booking details form
    if (this.isTestBookingView) {
      this.loadBookingDetailsFormData(formData, newIssue);
    }

    // Other information form (optional)
    this.loadAdditionalInfoFormData(formData, newIssue);

    // Attachment form
    this.loadAttachmentFormData(formData, newIssue);

    // Save new issue
    if (newIssue.areAllSelectedFilesWithinMaxFileSize) {
      this.ajax
        .request(
          `${this.ajax.host}/apigateway/v1/app/paritymanager/insert?market_id=${this.hotelAndMarketService.market.id}&subscription_id=${this.newIssue?.subscription?.id}`,
          {
            method: 'POST',
            contentLength: formData.length,
            contentType: false,
            processData: false,
            mimeType: 'multipart/form-data',
            data: formData,
          },
        )
        .then((/* response */) => {
          this.usageAnalytics.track('Issue Added', {
            eventTrigger: 'add-issue-button',
            los: newIssue.los,
            pos: newIssue.pos.id,
            platform: PLATFORM_PRETTY_NAMES[newIssue.channel_platform],
          });

          this.uploadingIssue = false;
          this.toast.success(
            this.intl.t('parity_manager.upload_success').toString(),
            {
              showIcon: true,
            },
          );
          this.exitForm(true);
          // Force refresh
          this.router.refresh('market.parity.parity-manager');
        })
        .catch((error) => {
          this.uploadingIssue = false;

          if (error) {
            error.payload?.errors?.forEach((e) => {
              this.errorTracking.addBreadcrumb({
                message: 'Error while uploading issue',
                data: e,
              });
            });
            this.errorTracking.captureException(error);
          }

          this.alerting.showErrorAlert({
            text: getAPIErrorMessage(error, this.intl),
          });
        });
    }
  }

  @action
  setIssueProperty(property, newValue) {
    set(this, `newIssue.${property}`, newValue);
  }

  @action
  setIssuePropertyFromEvent(property, event) {
    this.setIssueProperty(property, event.target.value);
  }

  @action
  toggleValue(field, event) {
    this.setIssueProperty(field, event.target.checked);
  }
}
